import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useSolutions, useSubscriptions, useTenantOnboarding } from '../../api/plans';
import { useTenantList } from '../../api/tenants';
import {
  ROLE_LABELS,
  type Subscription,
  type Tenant,
  type TenantOnboarding,
} from '../../api/types';
import { can, useMe } from '../../auth/AuthContext';
import { subscriptionTone } from '../../components/plans/PlanCards';
import { EmptyState, ErrorState, Skeleton, SkeletonTable } from '../../components/states';
import { DataTable } from '../../components/table/DataTable';
import { TenantTeam } from '../../components/tenants/TenantTeam';
import {
  Alert,
  Badge,
  Button,
  Card,
  PageHeader,
  Section,
  formatDate,
  statusLabel,
  statusTone,
} from '../../components/ui';
import { Welcome } from './Welcome';

/**
 * Tenant-owner landing page, laid out for a customer who did not ask to
 * learn this interface: the verdict first ("Your organisation is live"),
 * then billing, the Balsam contact, the team and the solutions, each from
 * its own query. An account with no organisation yet sees <Welcome /> instead.
 */
export function TenantDashboard() {
  const me = useMe();
  const tenantId = me.principal.tenantId;
  const hasTenant = tenantId !== null;
  const canSeeBilling = can(me, 'tenant:billing:read');

  const subscriptionsQuery = useSubscriptions(tenantId, { enabled: canSeeBilling });
  const solutionsQuery = useSolutions({ enabled: hasTenant && canSeeBilling });
  const onboardingQuery = useTenantOnboarding(tenantId, { enabled: canSeeBilling });
  const tenantQuery = useTenantList({ limit: 1 }, { enabled: hasTenant });

  const tenant = tenantQuery.data?.data[0] ?? null;
  const subscriptions = subscriptionsQuery.data ?? null;
  const solutions = solutionsQuery.data ?? null;
  const liveSubscription = (slug: string) =>
    subscriptions?.find((s) => s.solution === slug && s.status !== 'CANCELED') ?? null;
  const platformPlan = liveSubscription('platform');
  // A checklist that fails to load leaves the progress blank; the plan still shows.
  const progress = onboardingQuery.data?.progress ?? null;

  if (!hasTenant) return <Welcome />;

  const contactHref = tenant?.accountManager ? `mailto:${tenant.accountManager.email}` : null;

  return (
    <>
      <PageHeader eyebrow={tenant?.name ?? 'Portal'} title="Overview" />

      {!me.user.isEmailVerified || (me.user.phone && !me.user.isPhoneVerified) ? (
        <Alert kind="info">
          {!me.user.isEmailVerified ? 'Your email address is not verified yet. ' : null}
          {me.user.phone && !me.user.isPhoneVerified ? (
            <>
              Verify your phone number to enable sign-in by text message.{' '}
              <Link to="/account">Verify now</Link>
            </>
          ) : null}
        </Alert>
      ) : null}

      {can(me, 'tenant:application:read') ? (
        <Verdict
          query={tenantQuery}
          tenant={tenant}
          contactHref={contactHref}
          canSeePlans={canSeeBilling}
          hasPlan={!!platformPlan}
        />
      ) : null}

      <div className="grid grid--2">
        {canSeeBilling ? (
          <Card
            title="Billing"
            actions={
              can(me, 'tenant:billing:update') ? (
                <Button
                  variant="secondary"
                  className="btn--small"
                  disabled
                  title="Stripe billing arrives next"
                >
                  Update billing method
                </Button>
              ) : (
                <span className="muted">Owners update the billing method</span>
              )
            }
          >
            <BillingSummary
              plan={platformPlan}
              progress={progress}
              loading={subscriptionsQuery.isPending}
              error={subscriptionsQuery.isError ? subscriptionsQuery.error : null}
              onRetry={() => subscriptionsQuery.refetch()}
              retrying={subscriptionsQuery.isFetching}
            />
          </Card>
        ) : null}

        <Card title="Your Balsam contact">
          {tenantQuery.isPending ? <Skeleton kind="line" width="70%" /> : null}
          {tenant?.accountManager ? (
            <dl className="kv">
              <dt>Email</dt>
              <dd>
                <a href={`mailto:${tenant.accountManager.email}`}>{tenant.accountManager.email}</a>
              </dd>
              <dt>Role</dt>
              <dd>{ROLE_LABELS[tenant.accountManager.role]}</dd>
            </dl>
          ) : null}
          {tenant && !tenant.accountManager ? (
            <p className="muted">Balsam will assign your account manager shortly.</p>
          ) : null}
        </Card>
      </div>

      {can(me, 'tenant:team:read') ? (
        <TenantTeam
          tenantId={tenantId}
          canManage={can(me, 'tenant:team:manage')}
          selfUserId={me.user.id}
        />
      ) : null}

      {canSeeBilling ? (
        <Section title="Solutions" meta="What Balsam runs for you, and who provides it">
          {solutionsQuery.isError ? (
            <ErrorState
              error={solutionsQuery.error}
              onRetry={() => solutionsQuery.refetch()}
              retrying={solutionsQuery.isFetching}
            />
          ) : null}
          {solutionsQuery.isPending ? <SkeletonTable rows={2} cols={4} /> : null}
          {solutions && solutions.length === 0 ? (
            <EmptyState
              title="No solutions yet"
              body="What Balsam runs for your organisation appears here once it is set up."
            />
          ) : null}
          {solutions && solutions.length > 0 ? (
            <DataTable
              label="Solutions"
              columns={[
                { key: 'solution', header: 'Solution' },
                { key: 'provider', header: 'Provider' },
                { key: 'plan', header: 'Plan' },
                { key: 'status', header: 'Status' },
              ]}
            >
              {/* The platform plan itself sits in the Billing card. */}
              {solutions
                .filter((s) => s.slug !== 'platform')
                .map((s) => {
                  const sub = liveSubscription(s.slug);
                  return (
                    <tr key={s.slug}>
                      <td>
                        {s.slug === 'whatsapp' ? (
                          <Link to="/portal/whatsapp">{s.name}</Link>
                        ) : s.slug === 'email' ? (
                          <Link to="/portal/email">{s.name}</Link>
                        ) : (
                          s.name
                        )}
                      </td>
                      <td>{s.provider}</td>
                      <td>{sub ? sub.plan.name : s.includedWithOnboarding ? 'Included' : '—'}</td>
                      <td>
                        {sub ? (
                          <Badge tone={subscriptionTone(sub.status)}>
                            {statusLabel(sub.status)}
                          </Badge>
                        ) : s.includedWithOnboarding ? (
                          <Badge tone="accent">Included</Badge>
                        ) : (
                          <Badge tone="neutral">Not enabled</Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </DataTable>
          ) : null}
        </Section>
      ) : null}
    </>
  );
}

const VERDICT: Record<
  Tenant['status'],
  { title: (t: Tenant) => string; detail: string; next: 'plans' | 'contact' | null }
> = {
  ACTIVE: {
    title: () => 'Your organisation is live',
    detail:
      'Everything Balsam runs for you is on; your team, email and WhatsApp are in the sidebar.',
    next: 'plans',
  },
  PENDING: {
    title: (t) => `Under review since ${formatDate(t.createdAt)}`,
    detail: 'Balsam is setting your organisation up. You will be emailed when it goes live.',
    next: 'contact',
  },
  SUSPENDED: {
    title: () => 'Access is paused',
    detail: 'Your organisation is on hold. Your Balsam contact can tell you why and lift it.',
    next: 'contact',
  },
  CLOSED: {
    title: () => 'This organisation is closed',
    detail: 'Its data is kept but nothing runs. Talk to Balsam to reopen it.',
    next: 'contact',
  },
};

/** The one sentence the page exists for, large and first, with the one next step. */
function Verdict({
  query,
  tenant,
  contactHref,
  canSeePlans,
  hasPlan,
}: {
  query: ReturnType<typeof useTenantList>;
  tenant: Tenant | null;
  contactHref: string | null;
  canSeePlans: boolean;
  hasPlan: boolean;
}) {
  if (query.isError) {
    return (
      <ErrorState
        title="Could not load your organisation"
        error={query.error}
        onRetry={() => query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (query.isPending) {
    return (
      <div className="verdict" role="status" aria-label="Loading">
        <Skeleton kind="block" width="18rem" height="2rem" />
        <Skeleton kind="line" width="60%" />
      </div>
    );
  }
  if (!tenant) {
    return (
      <EmptyState
        title="No organisation yet"
        body="Your account is ready, but no organisation has been created for it. Balsam staff onboard new organisations; yours appears here once that is done."
      />
    );
  }
  const copy = VERDICT[tenant.status] ?? VERDICT.PENDING;
  let next: ReactNode = null;
  if (copy.next === 'plans' && canSeePlans) {
    next = <Link to="/portal/subscription">{hasPlan ? 'See your plan' : 'See the plans'}</Link>;
  } else if (copy.next === 'contact' && contactHref) {
    next = <a href={contactHref}>Email your Balsam contact</a>;
  }
  return (
    <section className="verdict" aria-labelledby="verdict-title">
      <p className="verdict__status">
        <Badge tone={statusTone(tenant.status)}>{statusLabel(tenant.status, 'portal')}</Badge>
      </p>
      <h2 className="verdict__title" id="verdict-title">
        {copy.title(tenant)}
      </h2>
      <p className="verdict__detail">
        {copy.detail}
        {next ? <> {next}</> : null}
      </p>
    </section>
  );
}

/** The platform plan and how far onboarding has come, or the way to a plan. No prices here. */
function BillingSummary({
  plan,
  progress,
  loading,
  error,
  onRetry,
  retrying,
}: {
  plan: Subscription | null;
  progress: TenantOnboarding['progress'] | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  retrying: boolean;
}) {
  if (error) return <ErrorState error={error} onRetry={onRetry} retrying={retrying} />;
  if (loading) {
    return (
      <>
        <Skeleton kind="block" width="60%" />
        <Skeleton kind="line" width="40%" />
      </>
    );
  }
  if (!plan) {
    return (
      <>
        <p className="billing__plan">No plan yet</p>
        <p className="muted">
          <Link to="/portal/subscription">Subscription</Link> shows what each plan includes; your
          Balsam contact switches one on.
        </p>
      </>
    );
  }
  const trial = plan.status === 'TRIALING' && plan.trialEnd;
  return (
    <>
      <dl className="kv">
        <dt>Plan</dt>
        <dd>
          <strong>{plan.plan.name}</strong>{' '}
          <Badge tone={subscriptionTone(plan.status)}>{statusLabel(plan.status)}</Badge>
        </dd>
        <dt>Onboarding</dt>
        <dd>{progress ? `${progress.activated} of ${progress.total} activated` : '—'}</dd>
        <dt>{trial ? 'Trial ends' : 'Renews'}</dt>
        <dd>{formatDate(trial ? plan.trialEnd : plan.currentPeriodEnd)}</dd>
      </dl>
      <p className="muted">
        <Link to="/portal/subscription">Subscription</Link> shows what the plan includes and the
        onboarding checklist.
      </p>
    </>
  );
}
